import { useEffect, useRef, useState } from "react";
import type { GearList } from "../../../api/client";
import { ConfirmationBox } from "./ConfirmationBox";
import { InfoDisplay, type Selection } from "./InfoDisplay";

/** The left column's list of lists: select, add, rename (double-click or the x button), delete, logout. */
export function ListsView({
  lists,
  selectedId,
  selection,
  unitId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  onDropItems,
  onLogout,
}: {
  lists: GearList[];
  selectedId: string | null;
  selection: Selection;
  unitId: number;
  onSelect: (id: string) => void;
  onAdd: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onDropItems: (listId: string, itemIds: string[]) => void;
  onLogout: () => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      const range = document.createRange();
      range.selectNodeContents(editRef.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [editingId]);

  function endEdit(id: string) {
    const name = editRef.current?.textContent?.trim() ?? "";
    setEditingId(null);
    const list = lists.find((l) => l.id === id);
    if (list && name && name !== list.name) onRename(id, name);
  }

  return (
    <div className="ListsView">
      <div className="listHolder">
        <ul id="gearListElement">
          {lists.map((list) => (
            <li
              key={list.id}
              id={`LiGearList${list.id}`}
              className="gearList"
              style={selectedId === list.id ? { backgroundColor: "#465065" } : undefined}
              onClick={() => editingId !== list.id && onSelect(list.id)}
              onDoubleClick={() => setEditingId(list.id)}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.backgroundColor = "#465065";
              }}
              onDragLeave={(e) => {
                if (selectedId !== list.id) e.currentTarget.style.backgroundColor = "";
              }}
              onDrop={(e) => {
                e.preventDefault();
                const ids = e.dataTransfer.getData("text/gg-items");
                if (ids) onDropItems(list.id, ids.split(","));
                if (selectedId !== list.id) e.currentTarget.style.backgroundColor = "";
              }}
            >
              <a
                href="#edit"
                className="editListButton"
                title="Rename"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingId(list.id);
                }}
              >
                x
              </a>
              <div
                ref={editingId === list.id ? editRef : undefined}
                className="editable"
                spellCheck={false}
                title={list.name}
                contentEditable={editingId === list.id}
                suppressContentEditableWarning
                style={editingId === list.id ? { color: "#333333" } : undefined}
                onBlur={() => editingId === list.id && endEdit(list.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    endEdit(list.id);
                  }
                  if (e.key === "Escape") setEditingId(null);
                }}
              >
                {list.name}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <InfoDisplay selection={selection} unitId={unitId} />
      <div className="controls">
        <a className="controlButton" href="#add" title="Add List" onClick={(e) => (e.preventDefault(), setAdding(true))}>
          +
        </a>
        <a
          className="controlButton"
          href="#delete"
          title="Delete Selected List"
          onClick={(e) => (e.preventDefault(), selectedId && setConfirmDelete(true))}
        >
          -
        </a>
        <a className="controlButton logoutButton" href="#logout" title="Logout" onClick={(e) => (e.preventDefault(), onLogout())}>
          Logout
        </a>
      </div>
      {adding && (
        <ConfirmationBox
          heading="New List"
          confirmLabel="Save"
          onCancel={() => (setAdding(false), setNewName(""))}
          onConfirm={() => {
            if (newName.trim()) onAdd(newName.trim());
            setAdding(false);
            setNewName("");
          }}
        >
          <p>
            <input
              autoFocus
              aria-label="List name"
              placeholder="List name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) {
                  onAdd(newName.trim());
                  setAdding(false);
                  setNewName("");
                }
              }}
            />
          </p>
        </ConfirmationBox>
      )}
      {confirmDelete && selectedId && (
        <ConfirmationBox
          heading="Delete List"
          message="Are you sure you want to delete this list? The items stay in your library."
          confirmLabel="Delete"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            onDelete(selectedId);
          }}
        />
      )}
    </div>
  );
}
